import { allGeoAreasSelector, createGeoAreaByIdSelector } from '@/redux/modules/geoArea/geoArea.selectors';
import { APIProvider } from '@vis.gl/react-google-maps';
import { AuctionsNearMeCardsViewNew } from './components/MapView/components/CardsView/AuctionsNearMeCardsViewNew';
import { AuctionsNearMeGoogleMap, MapBounds } from './components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import { AuctionsNearMeQueryParams, AuctionsNearMeRouteParams } from './types/AuctionsNearMe.types';
import { AuctionsNearMeSearchInput } from './components/MapView/components/SearchInput/AuctionsNearMeSearchInput';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { createBoundingBoxString } from './helpers/createBoundingBoxString';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
    defaultHitsPerPage,
    defaultPageNumber,
} from './constants/AuctionsNearMeConstants';
import { fetchCatalogsNearMeByBounds } from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE } from './constants/featureFlags';
import { findGeoAreaFromUrlParams } from './helpers/geoAreaToUrlParams';
import {
    getCatalogsNearMeSelector,
    getPaginationInfoSelector,
} from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { getGoogleApiKey } from '@/redux/modules/config';
import { getLocale } from '@/redux/modules/intl';
import { getPageDescriptionFromGeoArea } from './helpers/getPageDescription';
import { getPageTitle } from './helpers/getPageTitle';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { recordAnalyticsForNearMePageView } from '@/redux/modules/analytics';
import { RelatedGeoAreasNew } from '@/components/RelatedGeoAreas/RelatedGeoAreasNew';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useFetchCatalogsRelatedGeoArea } from './helpers/useFetchCatalogsRelatedGeoArea';
import { useGeocodeCustomRelatedGeoArea } from './helpers/useGeocodeCustomRelatedGeoArea';
import { useGeocodeCustomSearch } from './helpers/useGeocodeCustomSearch';
import { useGetQueryParams } from '@/hooks/queryParams/useGetQueryParams';
import { useLocation, useParams } from 'react-router-dom';
import { usePaginatedCatalogFetch } from './helpers/usePaginatedCatalogFetch';
import { waitToDispatch } from '@/utils/waitToDispatch/waitToDispatch';
import AuctionsNearMeMapView from './components/MapView/AuctionsNearMeMapView';
import AuctionsNearMePageMeta from './meta/AuctionsNearMePageMeta';
import CategoryAd from '@/components/Ads/CategoryAd';
import PageTitle from '@/components/PageTitle/PageTitle';
import styled from 'styled-components';

const AuctionsNearMePage = (): ReactElement => {
    // TODO: Replace with OpenFeature when POC is merged.
    const useNewAuctionsNearMePage = FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE;
    const params = useParams<AuctionsNearMeRouteParams>();
    const getQueryParams = useGetQueryParams<AuctionsNearMeQueryParams>();
    const queryParams = getQueryParams();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [geocoderFailed, setGeocoderFailed] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasFilteredResults, setHasFilteredResults] = useState(true);
    const [googleMapsReady, setGoogleMapsReady] = useState(false);
    const [customSearchBounds, setCustomSearchBounds] = useState<MapBounds | null>(null);

    // Initialize isGeocoding to true if we have a search query param (direct navigation to custom search url)
    // This prevents the initial camera change from firing before we geocode
    const shouldStartGeocoding = useNewAuctionsNearMePage && Boolean(queryParams.search) && !customSearchBounds;
    const [isGeocoding, setIsGeocoding] = useState(shouldStartGeocoding);

    // Track last fetch params to avoid duplicate fetches
    const lastFetchParamsRef = useRef<{ bounds: string; page: number; pageSize: number } | null>(null);

    // Track current active bounds (from map, search, or geoArea) for pagination to use
    const currentActiveBoundsRef = useRef<MapBounds | null>(null);

    const allGeoAreas = useAppSelector(allGeoAreasSelector);
    const googleApiKey = useAppSelector(getGoogleApiKey);
    const locale = useAppSelector(getLocale);
    const catalogsNearMe = useAppSelector(getCatalogsNearMeSelector);
    const paginationInfo = useAppSelector(getPaginationInfoSelector);
    const catalogIds = useMemo(() => catalogsNearMe.map((catalog) => catalog.id), [catalogsNearMe]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(defaultPageNumber);
    const [pageSize, setPageSize] = useState(defaultHitsPerPage);

    // Track previous pagination values to detect actual changes (skip initial mount)
    const prevPaginationRef = useRef({ page: currentPage, pageSize: pageSize });

    // Determine if the URL params are for the new URL format
    const hasNewUrlParams =
        ('city' in params && params.city) ||
        ('country' in params && params.country) ||
        ('state' in params && params.state);
    const hasOldUrlParams = 'geoAreaId' in params && params.geoAreaId;

    // Determine URL format based on params structure
    const isNewUrlFormat = useNewAuctionsNearMePage && hasNewUrlParams;

    // Get GeoArea from old URL format
    // Pass 0 for new URL format
    const geoAreaIdFromUrl = hasOldUrlParams ? Number(params.geoAreaId) : 0;
    const geoAreaFromOldUrl = useAppSelector(createGeoAreaByIdSelector(geoAreaIdFromUrl));

    // Get GeoArea from new URL format by matching params
    const geoAreaFromNewUrl = isNewUrlFormat
        ? findGeoAreaFromUrlParams(allGeoAreas, {
              city: params.city,
              country: params.country,
              state: params.state,
          })
        : undefined;

    // Use the appropriate GeoArea based on URL format
    const geoArea = isNewUrlFormat ? geoAreaFromNewUrl : geoAreaFromOldUrl;

    // Determine default map bounds based on user's locale
    const defaultBounds = useMemo(
        () => (locale === 'en-gb' ? defaultAuctionsNearMeUkBounds : defaultAuctionsNearMeUsaBounds),
        [locale]
    );

    useEffect(() => {
        waitToDispatch(recordAnalyticsForNearMePageView, dispatch, location);
    }, [dispatch, location]);

    // Pagination handlers
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        // Reset to page 1 when page size changes
        setCurrentPage(defaultPageNumber);
    };

    // FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE
    // Clear custom search bounds when navigating to a predefined geo area (not for query param changes)
    useEffect(() => {
        if (hasNewUrlParams || hasOldUrlParams) {
            setCustomSearchBounds(null);
            setSearchTerm('');
            setIsGeocoding(false);
            setCurrentPage(defaultPageNumber);
            // Clear ref so pagination falls back to geoArea bounds
            currentActiveBoundsRef.current = null;
        }
    }, [hasNewUrlParams, hasOldUrlParams]);

    // FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE
    // Fetch catalogs when navigating to a predefined geoArea via RelatedGeoAreas links
    // This handles clicking a RelatedGeoAreas ("More Locations" in the UI) link to a location that
    // is in our predefined geoAreas list (e.g., Miami, FL)
    // e.g., clicking "Miami, FL" RelatedGeoAreas link -> /find-auctions-near-me/fl/miami -> fetch catalogs for Miami, FL
    useFetchCatalogsRelatedGeoArea({
        currentActiveBoundsRef,
        currentPage,
        customSearchBounds,
        geoArea,
        lastFetchParamsRef,
        pageSize,
        queryParams,
        useNewAuctionsNearMePage,
    });

    // FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE
    // Geocode custom search term from query params
    // This only runs for direct navigation (e.g., /find-auctions-near-me?search=Cleveland)
    // If customSearchBounds already exists, it means we came from SearchInput, so skip geocoding
    useGeocodeCustomSearch({
        currentActiveBoundsRef,
        currentPage,
        customSearchBounds,
        dispatch,
        googleMapsReady,
        pageSize,
        queryParams,
        setCustomSearchBounds,
        setGeocoderFailed,
        setIsGeocoding,
        setSearchTerm,
        useNewAuctionsNearMePage,
    });

    // FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE
    // Geocode city/state from URL params when NOT in predefined geoAreas list
    // This handles RelatedGeoAreas ("More Locations" in the UI) links to custom locations
    // e.g., clicking "Patchogue, NY" RelatedGeoAreas link -> /find-auctions-near-me/ny/patchogue -> geocode Patchogue, NY
    useGeocodeCustomRelatedGeoArea({
        currentActiveBoundsRef,
        currentPage,
        customSearchBounds,
        geoArea,
        googleMapsReady,
        hasNewUrlParams: Boolean(hasNewUrlParams),
        pageSize,
        routeParams: params,
        setCustomSearchBounds,
        setGeocoderFailed,
        setIsGeocoding,
        setSearchTerm,
        useNewAuctionsNearMePage,
    });

    // FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE
    // Update Page Title based on filtered results
    useEffect(() => {
        if (useNewAuctionsNearMePage) {
            setHasFilteredResults(catalogsNearMe.length > 0);
        }
    }, [useNewAuctionsNearMePage, catalogsNearMe]);

    // FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE
    // Fetch catalogs when pagination or page size changes
    // Does NOT run on initial mount - map/search/geoArea handle initial fetch
    usePaginatedCatalogFetch({
        currentActiveBoundsRef,
        currentPage,
        customSearchBounds,
        defaultBounds,
        dispatch,
        geoArea,
        isGeocoding,
        lastFetchParamsRef,
        pageSize,
        prevPaginationRef,
        useNewAuctionsNearMePage,
    });

    const adId = 'auctions-near-me';
    const adPageName = `leaderboard-${adId}`;

    // Determine map bounds in priority order: customSearchBounds > geoArea bounds > default bounds
    let mapBounds = defaultBounds;

    if (customSearchBounds) {
        mapBounds = customSearchBounds;
    } else if (geoArea && geoArea.northEastLatitude && geoArea.southWestLatitude) {
        mapBounds = {
            northEast: {
                lat: geoArea.northEastLatitude,
                lng: geoArea.northEastLongitude,
            },
            southWest: {
                lat: geoArea.southWestLatitude,
                lng: geoArea.southWestLongitude,
            },
        };
    }

    if (useNewAuctionsNearMePage) {
        return (
            <>
                <CategoryAd
                    adType={LeaderBoardAdTypes[adId]}
                    pageName={adPageName}
                />
                <AuctionsNearMePageMeta
                    geoArea={geoArea}
                    locationName={geoArea?.geoAreaName}
                    searchTerm={searchTerm}
                />
                <section className="relative mx-auto flex min-h-[400px] max-w-[1240px] flex-col px-md pb-xl mdMax:ml-0 mdMax:min-h-[300px] mdMax:min-w-[55%] mdMax:max-w-full">
                    {/* Page Title and Description */}
                    <section className="mb-lg">
                        <div className="mb-sm flex w-full justify-between">
                            <PageTitle
                                className="mt-lg"
                                title={getPageTitle({ geoArea, geocoderFailed, hasFilteredResults, searchTerm })}
                            />
                        </div>
                        <div className="max-w-[590px]">
                            <BodySecondary color="secondary">{getPageDescriptionFromGeoArea(geoArea)}</BodySecondary>
                        </div>
                    </section>
                    {/* Split container */}
                    <section className="mb-sm flex gap-lg">
                        <div>
                            <AuctionsNearMeCardsViewNew
                                catalogIds={catalogIds}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                                pageSize={pageSize}
                                totalRecords={paginationInfo.totalRecords}
                            />
                            {/* Related Geo Areas (otherPlaces) Section */}
                            <RelatedGeoAreasNew geoArea={geoArea} />
                        </div>
                        {/* Map Section */}
                        <div>
                            <APIProvider apiKey={googleApiKey}>
                                <div className="w-full">
                                    <div className="mb-md">
                                        <AuctionsNearMeSearchInput
                                            currentGeoArea={geoArea}
                                            onSearchResults={({ northEast, southWest }) => {
                                                const bounds = { northEast, southWest };
                                                const insideBoundingBox = createBoundingBoxString(bounds);

                                                // Store bounds in ref for pagination to use
                                                currentActiveBoundsRef.current = bounds;

                                                // Update custom search bounds to trigger map zoom
                                                setCustomSearchBounds(bounds);

                                                // Reset to page 1 for new search
                                                setCurrentPage(defaultPageNumber);

                                                dispatch(
                                                    fetchCatalogsNearMeByBounds({
                                                        insideBoundingBox,
                                                        page: defaultPageNumber,
                                                        pageSize: pageSize,
                                                    })
                                                );
                                            }}
                                            setGeocoderFailed={setGeocoderFailed}
                                            setPageTitleSearchTerm={setSearchTerm}
                                        />
                                    </div>
                                    <AuctionsNearMeGoogleMap
                                        bounds={mapBounds}
                                        catalogs={catalogsNearMe}
                                        isGeocoding={isGeocoding}
                                        onBoundsChange={({ northEast, southWest }) => {
                                            const bounds = { northEast, southWest };

                                            // Store bounds in ref for pagination to use
                                            currentActiveBoundsRef.current = bounds;

                                            const insideBoundingBox = createBoundingBoxString(bounds);

                                            // Reset to page 1 when map bounds change (keep pageSize)
                                            setCurrentPage(defaultPageNumber);

                                            dispatch(
                                                fetchCatalogsNearMeByBounds({
                                                    insideBoundingBox,
                                                    page: defaultPageNumber,
                                                    pageSize: pageSize,
                                                })
                                            );
                                        }}
                                        onLoad={() => {
                                            setGoogleMapsReady(true);
                                        }}
                                    />
                                </div>
                            </APIProvider>
                        </div>
                    </section>
                </section>
            </>
        );
    }

    return (
        <>
            <CategoryAd
                adType={LeaderBoardAdTypes[adId]}
                pageName={adPageName}
            />
            <StyledContent>
                <AuctionsNearMePageMeta
                    geoArea={geoArea}
                    locationName={geoArea?.geoAreaName}
                />
                <StyledPageInformation>
                    <StyledTitleRow>
                        <PageTitle
                            className="mt-lg"
                            title={getPageTitle({ geoArea, geocoderFailed, hasFilteredResults, searchTerm })}
                        />
                    </StyledTitleRow>
                    <StyledDescription>
                        <BodySecondary color="secondary">{getPageDescriptionFromGeoArea(geoArea)}</BodySecondary>
                    </StyledDescription>
                </StyledPageInformation>
                <AuctionsNearMeMapView
                    geoArea={geoArea}
                    geocoderFailed={geocoderFailed}
                    setGeocoderFailed={setGeocoderFailed}
                    setHasFilteredResults={setHasFilteredResults}
                    setPageTitleSearchTerm={setSearchTerm}
                />
            </StyledContent>
        </>
    );
};

export default AuctionsNearMePage;

const StyledContent = styled.section`
    display: flex;
    position: relative;
    flex-direction: column;
    min-height: 400px;
    padding: 0 20px 50px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;
    @media (max-width: 1200px) {
        margin-left: 0;
        min-width: 55%;
        max-width: 100%;
        min-height: 300px;
    }
`;

const StyledPageInformation = styled.section`
    margin-bottom: 32px;
`;

const StyledTitleRow = styled.div`
    margin-bottom: 8px;
    width: 100%;
    display: flex;
    justify-content: space-between;
`;

const StyledDescription = styled.div`
    max-width: 590px;
`;
